import * as React from 'react'
import {useLocation} from 'react-router'
import {getLoaderObjectSingletonInstance, ViewLoaderProxy} from './ViewLoader'
import {CircularProgress, Typography} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {useIsAppDrawerExpanded} from '../AppDrawerContext'
import {definedContextValueHookFactory} from '@oc/hooks'

export interface ViewLoaderContext {
    visible: boolean
    setVisible: (_: React.SetStateAction<boolean>) => void
    overWholePage: boolean
    setOverWholePage: (_: React.SetStateAction<boolean>) => void
    loadMessage: string
    setLoadMessage: (_: React.SetStateAction<string>) => void
}

const ViewLoadContext = React.createContext<ViewLoaderContext | null>(null)
const useViewLoadContext = definedContextValueHookFactory(ViewLoadContext, 'ViewLoadContext')

function ViewLoadContextProvider({children}: React.PropsWithChildren) {
    const [visible, setVisible] = React.useState(false)
    const [overWholePage, setOverWholePage] = React.useState(false)
    const [loadMessage, setLoadMessage] = React.useState('')

    return (
        <ViewLoadContext.Provider
            value={{visible, setVisible, overWholePage, setOverWholePage, loadMessage, setLoadMessage}}
        >
            {children}
            <ViewLoader />
        </ViewLoadContext.Provider>
    )
}

const useViewLoaderStyles = makeStyles<{top: number; left: number}>((theme, params) => ({
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        height: '100vh',
        width: '100vw',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: theme.zIndex.modal + 10,
        background: 'rgba(255, 255, 255, 0.5)',
        flexDirection: 'column'
    },
    isNotLoading: {
        display: 'none'
    },
    isNotOverWholePage: {
        marginTop: params.top,
        marginLeft: params.left,
        height: `calc(100vh - ${params.top}px)`,
        width: `calc(100vw - ${params.left}px)`
    }
}))

function ViewLoader() {
    const isAppDrawerExpanded = useIsAppDrawerExpanded()
    const isLoading = useIsLoading()
    const loadMessage = useLoadMessage()
    const overWholePage = useIsOverWholePage()

    const {classes, cx} = useViewLoaderStyles({
        top: document.getElementById('oc-app-bar')?.clientHeight ?? 0,
        left: isAppDrawerExpanded ? 280 : 83
    })

    const className = cx(classes.root, {
        [classes.isNotLoading]: !isLoading,
        [classes.isNotOverWholePage]: !overWholePage
    })

    return (
        <div className={className}>
            <CircularProgress id="view-loader-circular" />
            {(loadMessage && <Typography variant="h5">{loadMessage}</Typography>) || null}
        </div>
    )
}

function useIsLoading(): boolean {
    const {visible} = useViewLoadContext()

    return visible
}

function useLoadMessage(): string {
    const {loadMessage} = useViewLoadContext()

    return loadMessage
}

function useIsOverWholePage(): boolean {
    const {overWholePage} = useViewLoadContext()

    return overWholePage
}

function useViewLoader(): ViewLoaderProxy {
    const {setVisible, setLoadMessage, setOverWholePage} = useViewLoadContext()
    const location = useLocation()

    const loader = React.useMemo(
        () => getLoaderObjectSingletonInstance(setVisible, setLoadMessage, setOverWholePage, location.pathname),
        [setVisible, setLoadMessage, setOverWholePage, location.pathname]
    )

    return loader
}

export {ViewLoadContext, ViewLoadContextProvider, useViewLoader, useIsLoading, useLoadMessage, useIsOverWholePage}
